import { Injectable, inject } from '@angular/core';
import { catchError, filter, map, Observable, of, take } from 'rxjs';
import { BreadcrumbResolver } from './breadcrumb-resolver';
import { Breadcrumb } from '@tc-angular/design-system';
import { InspirationRouteSegments } from 'src/app/shared/constants';
import { INSPIRATION_BLOGS_FACADE } from 'src/app/core/services/facades';
import { FacadeReadParams } from 'src/app/core/services/facades/facade-read-params';
import { BasicDataResult } from 'src/app/core/services/data-orchestrator';
import { InspirationBlogDto } from 'src/app/shared/dtos/inspiration-blog/v5';
import { mapInspirationBlogDtosToModel } from 'src/app/shared/mappers/inspiration-blog.mapper';

@Injectable({ providedIn: 'root' })
export class InspirationBreadcrumbResolver implements BreadcrumbResolver {
  private readonly inspirationBlogsFacade = inject(INSPIRATION_BLOGS_FACADE);

  private readonly baseBreadcrumbs: Breadcrumb[] = [
    {
      label: 'nav.dashboard',
      routerLink: ['/'],
    },
    {
      label: 'nav.inspiration',
      routerLink: ['/', InspirationRouteSegments.Inspiration],
    },
  ];

  public resolve(params: unknown): Observable<Breadcrumb[]> {
    const id = this.extractId(params);

    if (!id) {
      return of(this.baseBreadcrumbs);
    }

    const readParams = {
      readFromCacheFirst: true,
      disableAutoRefresh: true,
    } satisfies FacadeReadParams;

    // TODO: we don't cache this data so this triggers a new API request
    return this.inspirationBlogsFacade.getInspirationBlogs(readParams).data$.pipe(
      filter((result: BasicDataResult<InspirationBlogDto[]>) => !result.loading),
      take(1),
      map((result: BasicDataResult<InspirationBlogDto[]>) =>
        result.error ? this.baseBreadcrumbs : this.mapDataToBreadcrumbs(id, result.data),
      ),
      catchError((err: unknown) => {
        console.error('Failed to resolve inspiration breadcrumb', err, params);
        return of(this.baseBreadcrumbs);
      }),
    );
  }

  private mapDataToBreadcrumbs(id: string, dtos: InspirationBlogDto[] | null | undefined): Breadcrumb[] {
    const blog = mapInspirationBlogDtosToModel(dtos ?? []).find((b) => b.id === id);

    if (!blog) {
      return this.baseBreadcrumbs;
    }

    return [
      ...this.baseBreadcrumbs,
      {
        label: blog.title,
        backNavigationEnabled: true,
        routerLink: undefined,
      } satisfies Breadcrumb,
    ];
  }

  private extractId(params: unknown): string | undefined {
    if (!params || typeof params !== 'object') {
      return undefined;
    }

    const p = params as Record<string, unknown>;
    const id = p[InspirationRouteSegments.BlogId];

    return typeof id === 'string' && id.length > 0 ? id : undefined;
  }
}
